import { MM_PER_LEGACY_UNIT } from "./fixed";


export const ATTR_GRID = 256;
/** Cells per side in one patch's attribute map. */

export const PATCH_LEGACY_UNITS = 25_600;
/** Legacy units spanned by one terrain patch. */

export const CELL_MM = 1000;
/** One attribute cell is one metre. */

export const PATCH_MM = PATCH_LEGACY_UNITS * 10;

// Flags as stored in the blob. These are the original's `sectree.h:25-31` bit
// values. BANPK remains separate from BLOCK because the server owns the combat
// rule and walking across a safe-zone boundary must remain possible
// (`battle.cpp:66-76`).
export const FLAG_BLOCKED = 1 << 0;
export const FLAG_WATER = 1 << 1;
export const FLAG_BANPK = 1 << 2;

const MAGIC = 0x4d335452; // 'M3TR', read big-endian so it is legible in a hexdump
const VERSION = 1;
const HEADER_BYTES = 32;

export const MAX_CELLS = 64 * 1024 * 1024;

export class TerrainError extends Error {}

export interface World {
  isBlocked(xMm: number, yMm: number): boolean;
  isWater(xMm: number, yMm: number): boolean;
  /** Whether a static collision object stops a step from one point to the next. */
  blocksStep?(x0Mm: number, y0Mm: number, x1Mm: number, y1Mm: number): boolean;
  /** Where a step the objects stop slides to instead, or null where it stands. */
  avoidStep?(x0Mm: number, y0Mm: number, dxMm: number, dyMm: number): [number, number] | null;
}

/** A PC's `COLLISION_TYPE_BODY` sphere (`GameType.h:97-99`), unattached so it
 *  rides the actor's own origin (`ActorInstanceCollisionDetection.cpp:224-245`). */
export const BODY_SPHERE_RADIUS_MM = 400; // Radius 40 (root/warrior_m.msm:1583), read at GameType.cpp:140
export const BODY_SPHERE_HEIGHT_MM = 1000; // Position 0 0 100 (root/warrior_m.msm:1584), read at GameType.cpp:142

export const TREE_BUCKET_MM = 4000; // spatial index cell; no 2014 number, the culling tree is not ported

interface TreeShape {
  readonly xMm: number;
  readonly yMm: number;
  /** Squared horizontal reach at the body sphere's height. */
  readonly reachSq: number;
  /** A sphere touches strictly inside its reach, a cylinder on it too. */
  readonly inclusive: boolean;
  /** Squared horizontal reach its slide solves against: a sphere's `reachSq`,
   *  a cylinder's full `(radius + r)^2` (`CollisionData.cpp:256,520-523`). */
  readonly slideSq: number;
}

type Vec3 = readonly [number, number, number];

/** One `.mdatr` plane as `BuildCollisionInstance` leaves it: the quad's first
 *  and last corner, its normal and the four inward edge normals
 *  (`CollisionData.cpp:67-81`). */
interface PlaneShape {
  readonly q0: Vec3;
  readonly q3: Vec3;
  readonly normal: Vec3;
  readonly inside: readonly [Vec3, Vec3, Vec3, Vec3];
}

const sub3 = (a: Vec3, b: Vec3): Vec3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const dot3 = (a: Vec3, b: Vec3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const cross3 = (a: Vec3, b: Vec3): Vec3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];

/** `D3DXVec3Normalize`, which leaves a zero vector zero. */
function unit3(v: Vec3): Vec3 {
  const length = Math.sqrt(dot3(v, v));
  return length === 0 ? [0, 0, 0] : [v[0] / length, v[1] / length, v[2] / length];
}

/** The collision objects of a map's placed trees and buildings, as the local
 *  player's body sphere meets them. Every tree's `.spt` spheres and cylinders
 *  and every building's `.mdatr` spheres, cylinders and planes are registered at
 *  the object's transform (`SpeedTreeWrapper.cpp:234-262`,
 *  `CollisionData.cpp:35-194`), as is the ground shadow of the box a building
 *  with no `.mdatr` gets (`Area.cpp:719-775`), and a moving actor is stopped by
 *  one it touches while getting closer to it (`PythonBackground.cpp:569-596`,
 *  `ActorInstanceCollisionDetection.cpp:684-713`). The body is taken to stand at
 *  the height of the object's own base. */
export class TreeCollision {
  private readonly buckets = new Map<string, TreeShape[]>();
  private readonly planeBuckets = new Map<string, PlaneShape[]>();
  private readonly boxBuckets = new Map<string, Corners[]>();

  constructor(
    cylinders: readonly (readonly number[])[],
    spheres: readonly (readonly number[])[],
    planes: readonly (readonly number[])[] = [],
    boxes: readonly (readonly number[])[] = [],
  ) {
    const r = BODY_SPHERE_RADIUS_MM;
    for (const [x, y, z, radius, height] of cylinders) {
      // `CollideCylinderVSDynamicSphere` (`CollisionData.cpp:451-468`): the closest
      // axis point is clamped to the cylinder's span, so the body's height
      // outside that span is the vertical part of the distance.
      const above = BODY_SPHERE_HEIGHT_MM - z;
      const gap = Math.max(0, -above, above - height);
      if (gap > r) continue; // `CollisionData.cpp:453-457`
      this.add({ xMm: x, yMm: y, reachSq: (radius + r) * (radius + r) - gap * gap, inclusive: true, slideSq: (radius + r) * (radius + r) });
    }
    for (const [x, y, z, radius] of spheres) {
      // `CSphereCollisionInstance::OnMovementCollisionDynamicSphere` (`CollisionData.cpp:229-240`).
      const dz = z - BODY_SPHERE_HEIGHT_MM;
      const reachSq = (radius + r) * (radius + r) - dz * dz;
      if (reachSq <= 0) continue;
      this.add({ xMm: x, yMm: y, reachSq, inclusive: false, slideSq: reachSq });
    }
    for (const quad of planes) {
      const q0: Vec3 = [quad[0], quad[1], quad[2]];
      const q1: Vec3 = [quad[3], quad[4], quad[5]];
      const q2: Vec3 = [quad[6], quad[7], quad[8]];
      const q3: Vec3 = [quad[9], quad[10], quad[11]];
      const line0 = unit3(sub3(q1, q0)); // CollisionData.cpp:67,71
      const line1 = unit3(sub3(q2, q0)); // CollisionData.cpp:68,72
      const line2 = unit3(sub3(q1, q3)); // CollisionData.cpp:69,73
      const line3 = unit3(sub3(q2, q3)); // CollisionData.cpp:70,74
      const normal = unit3(cross3(line0, line1)); // CollisionData.cpp:75-76
      const shape: PlaneShape = {
        q0,
        q3,
        normal,
        inside: [cross3(normal, line0), cross3(line1, normal), cross3(line2, normal), cross3(normal, line3)], // CollisionData.cpp:78-81
      };
      // A body can only be stopped where its sphere reaches past the quad's edges
      // by less than its radius (`CollisionData.cpp:316-319`).
      const xs = [quad[0], quad[3], quad[6], quad[9]];
      const ys = [quad[1], quad[4], quad[7], quad[10]];
      const cx0 = Math.floor((Math.min(...xs) - r) / TREE_BUCKET_MM);
      const cx1 = Math.floor((Math.max(...xs) + r) / TREE_BUCKET_MM);
      const cy0 = Math.floor((Math.min(...ys) - r) / TREE_BUCKET_MM);
      const cy1 = Math.floor((Math.max(...ys) + r) / TREE_BUCKET_MM);
      for (let cy = cy0; cy <= cy1; cy++) {
        for (let cx = cx0; cx <= cx1; cx++) {
          const key = `${cx},${cy}`;
          const list = this.planeBuckets.get(key);
          if (list) list.push(shape);
          else this.planeBuckets.set(key, [shape]);
        }
      }
    }
    for (const shadow of boxes) {
      const corners: [number, number][] = [];
      for (let at = 0; at < shadow.length; at += 2) corners.push([shadow[at], shadow[at + 1]]);
      const xs = corners.map(([x]) => x);
      const ys = corners.map(([, y]) => y);
      const cx0 = Math.floor((Math.min(...xs) - r) / TREE_BUCKET_MM);
      const cx1 = Math.floor((Math.max(...xs) + r) / TREE_BUCKET_MM);
      const cy0 = Math.floor((Math.min(...ys) - r) / TREE_BUCKET_MM);
      const cy1 = Math.floor((Math.max(...ys) + r) / TREE_BUCKET_MM);
      for (let cy = cy0; cy <= cy1; cy++) {
        for (let cx = cx0; cx <= cx1; cx++) {
          const key = `${cx},${cy}`;
          const list = this.boxBuckets.get(key);
          if (list) list.push(corners);
          else this.boxBuckets.set(key, [corners]);
        }
      }
    }
  }

  static fromJson(data: {
    cylinders?: readonly (readonly number[])[];
    spheres?: readonly (readonly number[])[];
    planes?: readonly (readonly number[])[];
    boxes?: readonly (readonly number[])[];
  }): TreeCollision {
    return new TreeCollision(data.cylinders ?? [], data.spheres ?? [], data.planes ?? [], data.boxes ?? []);
  }

  private add(shape: TreeShape): void {
    const reach = Math.ceil(Math.sqrt(shape.reachSq));
    const cx0 = Math.floor((shape.xMm - reach) / TREE_BUCKET_MM);
    const cx1 = Math.floor((shape.xMm + reach) / TREE_BUCKET_MM);
    const cy0 = Math.floor((shape.yMm - reach) / TREE_BUCKET_MM);
    const cy1 = Math.floor((shape.yMm + reach) / TREE_BUCKET_MM);
    for (let cy = cy0; cy <= cy1; cy++) {
      for (let cx = cx0; cx <= cx1; cx++) {
        const key = `${cx},${cy}`;
        const list = this.buckets.get(key);
        if (list) list.push(shape);
        else this.buckets.set(key, [shape]);
      }
    }
  }

  /** Whether the body moving from (x0, y0) to (x1, y1) is stopped. */
  blocksStep(x0Mm: number, y0Mm: number, x1Mm: number, y1Mm: number): boolean {
    const key = `${Math.floor(x1Mm / TREE_BUCKET_MM)},${Math.floor(y1Mm / TREE_BUCKET_MM)}`;
    for (const shape of this.buckets.get(key) ?? []) {
      if (touches(shape, x0Mm, y0Mm, x1Mm, y1Mm)) return true;
    }
    for (const plane of this.planeBuckets.get(key) ?? []) {
      if (crossesPlane(plane, x0Mm, y0Mm, x1Mm, y1Mm)) return true;
    }
    const reach = BODY_SPHERE_RADIUS_MM * BODY_SPHERE_RADIUS_MM;
    for (const corners of this.boxBuckets.get(key) ?? []) {
      const toSq = shadowDistanceSq(corners, x1Mm, y1Mm);
      if (toSq <= reach && toSq < shadowDistanceSq(corners, x0Mm, y0Mm)) return true; // CollisionData.cpp:757, 233-236
    }
    return false;
  }

  /** `CActorInstance::AvoidObject` for every shape in the cell of `(kx, ky)`
   *  (`ActorInstanceCollisionDetection.cpp:644-656`, `ActorInstance.cpp:677-714`):
   *  each shape the body's step touches clamps the movement to the slide speed,
   *  then adds that shape's `OnGetCollisionMovementAdjust` to the movement and to
   *  the body sphere. Returns whether any shape was touched. */
  avoid(x0: number, y0: number, kx: number, ky: number, step: AvoidStep): boolean {
    const key = `${Math.floor(kx / TREE_BUCKET_MM)},${Math.floor(ky / TREE_BUCKET_MM)}`;
    let hit = false;
    const apply = (delta: readonly [number, number]): void => {
      hit = true;
      clampSlide(step); // ActorInstance.cpp:689-691
      step.mx += delta[0]; // ActorInstance.cpp:702-703
      step.my += delta[1];
      step.px += delta[0];
      step.py += delta[1];
      if (delta[0] !== 0 || delta[1] !== 0) {
        if (clampSlide(step)) {
          step.px = x0 + step.mx; // ActorInstance.cpp:709-710
          step.py = y0 + step.my;
        }
      }
    };
    for (const shape of this.buckets.get(key) ?? []) {
      if (touches(shape, x0, y0, step.px, step.py)) apply(roundAdjust(shape, x0, y0, step.px, step.py));
    }
    for (const plane of this.planeBuckets.get(key) ?? []) {
      if (crossesPlane(plane, x0, y0, step.px, step.py)) apply(planeAdjust(plane, x0, y0, step.px, step.py));
    }
    const reach = BODY_SPHERE_RADIUS_MM * BODY_SPHERE_RADIUS_MM;
    for (const corners of this.boxBuckets.get(key) ?? []) {
      const toSq = shadowDistanceSq(corners, step.px, step.py);
      if (toSq <= reach && toSq < shadowDistanceSq(corners, x0, y0)) apply([0, 0]); // CollisionData.cpp:811-816
    }
    return hit;
  }
}

/** One frame's movement and body sphere while `AvoidObject` adjusts them:
 *  `m_v3Movement` and the sphere's `v3Position`, which part ways once the
 *  movement is clamped (`ActorInstance.cpp:689-714`). Millimetres, unrounded. */
export interface AvoidStep {
  mx: number;
  my: number;
  px: number;
  py: number;
}

/** `gc_fActorSlideMoveSpeed`, 5 units a frame (`ActorInstance.cpp:599`). */
export const SLIDE_SPEED_MM = 5 * MM_PER_LEGACY_UNIT; // ActorInstance.cpp:599

/** `gc_fReduceMove` (`CollisionData.cpp:8`). */
export const REDUCE_MOVE = 0.5; // CollisionData.cpp:8

/** Scale the movement down to the slide speed; whether it had to. */
function clampSlide(step: AvoidStep): boolean {
  const length = Math.sqrt(step.mx * step.mx + step.my * step.my);
  if (!(length > SLIDE_SPEED_MM)) return false; // ActorInstance.cpp:689-690,706-707
  step.mx = step.mx * (SLIDE_SPEED_MM / length);
  step.my = step.my * (SLIDE_SPEED_MM / length);
  return true;
}

/** `CSphereCollisionInstance`/`CCylinderCollisionInstance::OnGetCollisionMovementAdjust`
 *  (`CollisionData.cpp:254-276,514-541`) in the original's units: the body is
 *  pushed sideways along the step's perpendicular by half the root nearest zero. */
function roundAdjust(shape: TreeShape, x0: number, y0: number, x1: number, y1: number): [number, number] {
  const u = MM_PER_LEGACY_UNIT;
  const px = (x1 - shape.xMm) / u;
  const py = (y1 - shape.yMm) / u;
  const distSq = px * px + py * py;
  const slide = shape.slideSq / (u * u);
  if (distSq >= slide) return [0, 0]; // CollisionData.cpp:256-257,519-520
  const cx = (y1 - y0) / u; // D3DXVec3Cross(advance, z), CollisionData.cpp:259,525
  const cy = -(x1 - x0) / u;
  const sum = -(cx * px + cy * py); // CollisionData.cpp:261,527
  const mul = slide - distSq; // CollisionData.cpp:262,528
  if (sum * sum - 4 * mul <= 0) return [0, 0]; // CollisionData.cpp:264-265,530-531
  const sq = Math.sqrt(sum * sum - 4 * mul);
  const t1 = (-sum - sq) * 0.5; // CollisionData.cpp:266-269,532-535
  const t2 = (-sum + sq) * 0.5;
  const t = Math.abs(t1) <= Math.abs(t2) ? t1 : t2; // CollisionData.cpp:271-276,538-541
  return [REDUCE_MOVE * t * cx * u, REDUCE_MOVE * t * cy * u];
}

/** `CPlaneCollisionInstance::OnGetCollisionMovementAdjust` (`CollisionData.cpp:362-381`)
 *  on the ground: the step carried to the plane, then held off it by the body's
 *  radius along the normal. */
function planeAdjust(plane: PlaneShape, x0: number, y0: number, x1: number, y1: number): [number, number] {
  const u = MM_PER_LEGACY_UNIT;
  const advance: Vec3 = [(x1 - x0) / u, (y1 - y0) / u, 0];
  const d = dot3(plane.normal, advance); // CollisionData.cpp:366
  if (d >= -0.0001 && d <= 0.0001) return [0, 0]; // CollisionData.cpp:367-368
  const position: Vec3 = [x1 / u, y1 / u, BODY_SPHERE_HEIGHT_MM / u];
  const origin: Vec3 = [plane.q0[0] / u, plane.q0[1] / u, plane.q0[2] / u];
  const t = -dot3(plane.normal, sub3(position, origin)) / d; // CollisionData.cpp:369
  const r = BODY_SPHERE_RADIUS_MM / u;
  const side = d >= 0 ? -r : r; // CollisionData.cpp:373-381
  return [(t * advance[0] + side * plane.normal[0]) * u, (t * advance[1] + side * plane.normal[1]) * u];
}

/** `CPythonBackground::CheckAdvancing` for one frame's step of (dx, dy) from
 *  (x0, y0) (`PythonBackground.cpp:569-596`): every set of shapes adjusts the
 *  movement (`CollisionAdjustChecker`), and the adjusted body is tested once more
 *  (`CollisionChecker`); still blocked, it stands (`BlockMovement`), otherwise it
 *  moves by the adjusted movement, truncated to the millimetre. */
export function avoidObjects(
  sets: readonly (TreeCollision | null)[],
  x0: number,
  y0: number,
  dx: number,
  dy: number,
): [number, number] | null {
  const step: AvoidStep = { mx: dx, my: dy, px: x0 + dx, py: y0 + dy };
  let hit = false;
  for (const set of sets) {
    if (set !== null && set.avoid(x0, y0, x0 + dx, y0 + dy, step)) hit = true;
  }
  if (!hit) return [x0 + dx, y0 + dy];
  for (const set of sets) {
    if (set !== null && set.blocksStep(x0, y0, step.px, step.py)) return null; // PythonBackground.cpp:582-588
  }
  return [x0 + Math.trunc(step.mx), y0 + Math.trunc(step.my)];
}

/** A stand-in box's shadow on the ground, its corners counter-clockwise. */
type Corners = readonly (readonly [number, number])[];

/** The squared ground distance from (x, y) to a stand-in box's shadow, and 0
 *  inside it: the clamp to the box and the horizontal distance of
 *  `COBBCollisionInstance::OnMovementCollisionDynamicSphere`
 *  (`CollisionData.cpp:747-757`, `GrpMath.h:42-45`). Mirrors
 *  `_shadow_distance_sq` in `metin3/sim/collision.py` operation for operation. */
function shadowDistanceSq(corners: Corners, x: number, y: number): number {
  let inside = corners.length >= 3;
  let best = Infinity;
  for (let i = 0; i < corners.length; i++) {
    const [ax, ay] = corners[i];
    const [bx, by] = corners[(i + 1) % corners.length];
    const ex = bx - ax;
    const ey = by - ay;
    const px = x - ax;
    const py = y - ay;
    const cross = ex * py - ey * px;
    if (cross < 0) inside = false;
    const lengthSq = ex * ex + ey * ey;
    const dot = px * ex + py * ey;
    let d: number;
    if (dot <= 0 || lengthSq === 0) d = px * px + py * py;
    else if (dot >= lengthSq) d = (x - bx) * (x - bx) + (y - by) * (y - by);
    else d = (cross * cross) / lengthSq;
    best = Math.min(best, d);
  }
  return inside ? 0 : best;
}

/** `CPlaneCollisionInstance::OnMovementCollisionDynamicSphere`
 *  (`CollisionData.cpp:305-331`) for the body sphere stepping from (x0, y0) to
 *  (x1, y1). The quad's first corner stands for its centre: both lie in the
 *  plane, so their distances along the normal agree. */
function crossesPlane(plane: PlaneShape, x0: number, y0: number, x1: number, y1: number): boolean {
  const r = BODY_SPHERE_RADIUS_MM;
  const position: Vec3 = [x1, y1, BODY_SPHERE_HEIGHT_MM];
  const last: Vec3 = [x0, y0, BODY_SPHERE_HEIGHT_MM];
  const toPosition = sub3(position, plane.q0);
  const p1 = dot3(plane.normal, toPosition); // CollisionData.cpp:310
  const p2 = dot3(plane.normal, sub3(last, plane.q0)); // CollisionData.cpp:311
  if (!((p1 > 0 && p2 < 0) || (p1 < 0 && p2 > 0) || (p1 <= r && p1 >= -r))) return false; // CollisionData.cpp:313-314
  const fromLast = sub3(position, plane.q3); // CollisionData.cpp:317
  if (!(dot3(toPosition, plane.inside[0]) > -r)) return false; // CollisionData.cpp:319
  if (!(dot3(toPosition, plane.inside[1]) > -r)) return false; // CollisionData.cpp:320
  if (!(dot3(fromLast, plane.inside[2]) > -r)) return false; // CollisionData.cpp:321
  if (!(dot3(fromLast, plane.inside[3]) > -r)) return false; // CollisionData.cpp:322
  return Math.abs(p1) < Math.abs(p2); // CollisionData.cpp:325-327
}

function touches(shape: TreeShape, x0: number, y0: number, x1: number, y1: number): boolean {
  const ax = shape.xMm - x0;
  const ay = shape.yMm - y0;
  const bx = shape.xMm - x1;
  const by = shape.yMm - y1;
  const fromSq = ax * ax + ay * ay;
  const toSq = bx * bx + by * by;
  // Only while getting closer (`CollisionData.cpp:233-236,474-477`).
  if (!(toSq < fromSq)) return false;
  const dx = x1 - x0;
  const dy = y1 - y0;
  const lengthSq = dx * dx + dy * dy;
  const dot = ax * dx + ay * dy;
  // Squared distance from the centre to the swept segment, scaled by
  // `lengthSq` where the closest point is interior so it stays integral.
  let distSq: number;
  let scale = 1;
  if (dot <= 0 || lengthSq === 0) distSq = fromSq;
  else if (dot >= lengthSq) distSq = toSq;
  else {
    const cross = ax * dy - ay * dx;
    distSq = cross * cross;
    scale = lengthSq;
  }
  const reach = shape.reachSq * scale;
  return shape.inclusive ? distSq <= reach : distSq < reach;
}

/** `client/public/guildattributes.json`: each guild building vnum's `.mdatr`
 *  records in its model's frame, millimetres, and its height triangles. */
export interface GuildAttributeTable {
  readonly buildings: Readonly<Record<string, {
    readonly model: string;
    readonly cylinders: readonly (readonly number[])[];
    readonly spheres: readonly (readonly number[])[];
    readonly planes: readonly (readonly number[])[];
    readonly surface: { readonly radiusMm: number; readonly triangles: readonly number[] };
  }>>;
}

/** One standing guild building: its vnum, where it stands and its facing in
 *  the wire's 4096 steps. */
export interface StandingBuilding {
  readonly vnum: number;
  readonly xMm: number;
  readonly yMm: number;
  readonly heading: number;
}

/** Every standing guild building's `.mdatr` records placed at its transform, as
 *  `CActorInstance::UpdateAttribute` hands them to `AddCollision` with the
 *  actor's matrix (`ActorInstanceData.cpp:20-37`, `ActorInstance.cpp:505-514`).
 *  Local x and y turn by the heading and y flips into the world's; z stays above
 *  the building's own base; rounding is `metin3/sim/collision.py`'s `_half_up`. */
export function guildBuildingCollision(
  table: GuildAttributeTable,
  buildings: readonly StandingBuilding[],
): TreeCollision {
  const cylinders: number[][] = [];
  const spheres: number[][] = [];
  const planes: number[][] = [];
  for (const building of buildings) {
    const row = table.buildings[String(building.vnum)];
    if (row === undefined) continue;
    const angle = (building.heading * 2 * Math.PI) / 4096;
    const c = Math.cos(angle);
    const s = Math.sin(angle);
    const placed = (x: number, y: number): [number, number] => [
      Math.floor(building.xMm + (x * c - y * s) + 0.5),
      Math.floor(building.yMm - (x * s + y * c) + 0.5),
    ];
    for (const [x, y, z, radius, height] of row.cylinders) cylinders.push([...placed(x, y), z, radius, height]);
    for (const [x, y, z, radius] of row.spheres) spheres.push([...placed(x, y), z, radius]);
    for (const quad of row.planes) {
      const corners: number[] = [];
      for (let at = 0; at < 12; at += 3) corners.push(...placed(quad[at], quad[at + 1]), quad[at + 2]);
      planes.push(corners);
    }
  }
  return new TreeCollision(cylinders, spheres, planes);
}

export interface ObjectWorld extends World {
  /** Whether a guild-building footprint covers this cell (`sectree.h:30`). */
  isObject(xMm: number, yMm: number): boolean;
  /** Drop every runtime ATTR_OBJECT cell (`ATTR_REGION_MODE_REMOVE`). */
  clearObjectAttrs(): void;
  /** Mark every collision cell overlapping an AABB as ATTR_OBJECT. */
  stampObjectRect(x0Mm: number, y0Mm: number, x1Mm: number, y1Mm: number): void;
  /** The guild siting window's own ghost while its PC blocker would hold
   *  (`chr.SetInstanceType(chr.INSTANCE_TYPE_BUILDING)`, `root/uiguild.py:2370-2375`):
   *  a rectangle nobody has placed yet, so it is kept apart from the durable
   *  footprints `stampObjectRect` tracks and does not survive their reset. */
  setGhostFootprint(rect: { x0Mm: number; y0Mm: number; x1Mm: number; y1Mm: number } | null): void;
  /** The map's placed trees, or none. */
  setTrees(trees: TreeCollision | null): void;
  /** The standing guild buildings' own `.mdatr` shapes, or none. */
  setBuildings(buildings: TreeCollision | null): void;
  blocksStep(x0Mm: number, y0Mm: number, x1Mm: number, y1Mm: number): boolean;
  avoidStep(x0Mm: number, y0Mm: number, dxMm: number, dyMm: number): [number, number] | null;
}

class ObjectOverlay {
  private readonly cells = new Set<string>();
  private ghost: { cx0: number; cy0: number; cx1: number; cy1: number } | null = null;

  constructor(
    private readonly baseXMm: number,
    private readonly baseYMm: number,
    private readonly cellMm: number,
  ) {}

  private key(xMm: number, yMm: number): string {
    // Floor, matching Python's `//`: unlike `flagsAt` these coordinates are not
    // pre-screened for sign, and truncation would fold the two cells either side
    // of the origin into one.
    const cx = Math.floor((xMm - this.baseXMm) / this.cellMm);
    const cy = Math.floor((yMm - this.baseYMm) / this.cellMm);
    return `${cx},${cy}`;
  }

  private cellOf(xMm: number, yMm: number): { cx: number; cy: number } {
    return {
      cx: Math.floor((xMm - this.baseXMm) / this.cellMm),
      cy: Math.floor((yMm - this.baseYMm) / this.cellMm),
    };
  }

  has(xMm: number, yMm: number): boolean {
    // `isBlocked` runs three times a tick per predicted entity, and on most maps
    // there is no guild building and no open siting window, so the common case
    // must not cost a string allocation.
    if (this.cells.size > 0 && this.cells.has(this.key(xMm, yMm))) return true;
    if (this.ghost !== null) {
      const { cx, cy } = this.cellOf(xMm, yMm);
      if (cx >= this.ghost.cx0 && cx <= this.ghost.cx1 && cy >= this.ghost.cy0 && cy <= this.ghost.cy1) {
        return true;
      }
    }
    return false;
  }

  clear(): void {
    this.cells.clear();
  }

  stamp(x0Mm: number, y0Mm: number, x1Mm: number, y1Mm: number): void {
    const loX = Math.min(x0Mm, x1Mm);
    const hiX = Math.max(x0Mm, x1Mm);
    const loY = Math.min(y0Mm, y1Mm);
    const hiY = Math.max(y0Mm, y1Mm);
    const cx0 = Math.floor((loX - this.baseXMm) / this.cellMm);
    const cy0 = Math.floor((loY - this.baseYMm) / this.cellMm);
    const cx1 = Math.floor((hiX - this.baseXMm) / this.cellMm);
    const cy1 = Math.floor((hiY - this.baseYMm) / this.cellMm);
    for (let cy = cy0; cy <= cy1; cy++) {
      for (let cx = cx0; cx <= cx1; cx++) this.cells.add(`${cx},${cy}`);
    }
  }

  setGhost(rect: { x0Mm: number; y0Mm: number; x1Mm: number; y1Mm: number } | null): void {
    if (rect === null) {
      this.ghost = null;
      return;
    }
    const loX = Math.min(rect.x0Mm, rect.x1Mm);
    const hiX = Math.max(rect.x0Mm, rect.x1Mm);
    const loY = Math.min(rect.y0Mm, rect.y1Mm);
    const hiY = Math.max(rect.y0Mm, rect.y1Mm);
    const { cx: cx0, cy: cy0 } = this.cellOf(loX, loY);
    const { cx: cx1, cy: cy1 } = this.cellOf(hiX, hiY);
    this.ghost = { cx0, cy0, cx1, cy1 };
  }
}

export class OpenWorld implements ObjectWorld {
  // `OpenWorld()` in Python is `CollisionMap(patches_x=0, patches_y=0)` with
  // `attr_at` forced to 0, so its overlay origin is (0, 0) and its cell size the
  // default 1000 mm (`_object_step_mm`'s fallback). Same numbers here, for the
  // same reason: a stamp into an open world has to block on both sides.
  private readonly objects = new ObjectOverlay(0, 0, CELL_MM);

  isBlocked(xMm = 0, yMm = 0): boolean {
    return this.objects.has(xMm, yMm);
  }

  isWater(): boolean {
    return false;
  }

  isObject(xMm: number, yMm: number): boolean {
    return this.objects.has(xMm, yMm);
  }

  clearObjectAttrs(): void {
    this.objects.clear();
  }

  stampObjectRect(x0Mm: number, y0Mm: number, x1Mm: number, y1Mm: number): void {
    this.objects.stamp(x0Mm, y0Mm, x1Mm, y1Mm);
  }

  setGhostFootprint(rect: { x0Mm: number; y0Mm: number; x1Mm: number; y1Mm: number } | null): void {
    this.objects.setGhost(rect);
  }

  private trees: TreeCollision | null = null;
  private buildings: TreeCollision | null = null;

  setTrees(trees: TreeCollision | null): void {
    this.trees = trees;
  }

  setBuildings(buildings: TreeCollision | null): void {
    this.buildings = buildings;
  }

  /** A placed tree or a guild building's own shapes stop the step
   *  (`PythonBackground.cpp:571-597`, `ActorInstanceCollisionDetection.cpp:684-713`). */
  blocksStep(x0Mm: number, y0Mm: number, x1Mm: number, y1Mm: number): boolean {
    return (this.trees !== null && this.trees.blocksStep(x0Mm, y0Mm, x1Mm, y1Mm))
      || (this.buildings !== null && this.buildings.blocksStep(x0Mm, y0Mm, x1Mm, y1Mm));
  }

  avoidStep(x0Mm: number, y0Mm: number, dxMm: number, dyMm: number): [number, number] | null {
    return avoidObjects([this.trees, this.buildings], x0Mm, y0Mm, dxMm, dyMm);
  }
}

/** Attribute grid for a whole map, addressed in world millimetres. */
export class CollisionMap implements ObjectWorld {
  readonly patchesX: number;
  readonly patchesY: number;
  readonly baseXMm: number;
  readonly baseYMm: number;
  readonly cellMm: number;
  readonly grid: number;
  /** Cells per world row: `patchesX * grid`. Cached; it is used per query. */
  readonly worldCellsX: number;
  readonly worldCellsY: number;
  private readonly flags: Uint8Array;

  constructor(options: {
    patchesX: number;
    patchesY: number;
    flags: Uint8Array;
    baseXMm?: number;
    baseYMm?: number;
    cellMm?: number;
    grid?: number;
  }) {
    this.patchesX = options.patchesX;
    this.patchesY = options.patchesY;
    this.baseXMm = options.baseXMm ?? 0;
    this.baseYMm = options.baseYMm ?? 0;
    this.cellMm = options.cellMm ?? CELL_MM;
    this.grid = options.grid ?? ATTR_GRID;
    // `terrain_blob.py` refuses a degenerate cell size or grid outright. Without
    // the same refusal here `Math.floor(0 / 0)` is NaN, `NaN >= worldCellsX` is
    // false, and the origin cell reads as *walkable* -- a fail-open hole in a
    // decoder whose whole contract is to fail closed.
    if (!(this.cellMm > 0) || !(this.grid > 0)) {
      throw new TerrainError(
        `degenerate cell size ${this.cellMm} or grid ${this.grid}`,
      );
    }
    this.worldCellsX = this.patchesX * this.grid;
    this.worldCellsY = this.patchesY * this.grid;
    const expected = this.worldCellsX * this.worldCellsY;
    if (options.flags.length !== expected) {
      throw new TerrainError(
        `flag grid is ${options.flags.length} cells, expected ${expected}`,
      );
    }
    this.flags = options.flags;
    this.objects = new ObjectOverlay(this.baseXMm, this.baseYMm, this.cellMm);
  }

  /** Attribute flags at a world position. Out of bounds reads as blocked. */
  flagsAt(xMm: number, yMm: number): number {
    const lx = xMm - this.baseXMm;
    const ly = yMm - this.baseYMm;
    if (lx < 0 || ly < 0) return FLAG_BLOCKED;

    // Floor, not truncate: only reached with non-negative operands, but the two
    // must not be confused if a base offset ever moves the origin.
    const cx = Math.floor(lx / this.cellMm);
    const cy = Math.floor(ly / this.cellMm);
    if (cx >= this.worldCellsX || cy >= this.worldCellsY) return FLAG_BLOCKED;

    return this.flags[cy * this.worldCellsX + cx];
  }

  isBlocked(xMm: number, yMm: number): boolean {
    // File FLAG_BLOCKED plus the runtime ATTR_OBJECT overlay, the same pair
    // `collision.py`'s `is_blocked` tests (`char_manager.cpp:371-372`,
    // `sectree_manager.cpp:749`).
    return (
      (this.flagsAt(xMm, yMm) & FLAG_BLOCKED) !== 0 || this.isObject(xMm, yMm)
    );
  }

  isWater(xMm: number, yMm: number): boolean {
    return (this.flagsAt(xMm, yMm) & FLAG_WATER) !== 0;
  }

  isSafeZone(xMm: number, yMm: number): boolean {
    return (this.flagsAt(xMm, yMm) & FLAG_BANPK) !== 0;
  }


  private objects!: ObjectOverlay;

  /** Whether a guild-building footprint covers this cell (`sectree.h:30`). */
  isObject(xMm: number, yMm: number): boolean {
    return this.objects.has(xMm, yMm);
  }

  /** Drop every runtime ATTR_OBJECT cell (`ATTR_REGION_MODE_REMOVE`). */
  clearObjectAttrs(): void {
    this.objects.clear();
  }

  stampObjectRect(x0Mm: number, y0Mm: number, x1Mm: number, y1Mm: number): void {
    this.objects.stamp(x0Mm, y0Mm, x1Mm, y1Mm);
  }

  setGhostFootprint(rect: { x0Mm: number; y0Mm: number; x1Mm: number; y1Mm: number } | null): void {
    this.objects.setGhost(rect);
  }

  private trees: TreeCollision | null = null;
  private buildings: TreeCollision | null = null;

  setTrees(trees: TreeCollision | null): void {
    this.trees = trees;
  }

  setBuildings(buildings: TreeCollision | null): void {
    this.buildings = buildings;
  }

  /** A placed tree or a guild building's own shapes stop the step
   *  (`PythonBackground.cpp:571-597`, `ActorInstanceCollisionDetection.cpp:684-713`). */
  blocksStep(x0Mm: number, y0Mm: number, x1Mm: number, y1Mm: number): boolean {
    return (this.trees !== null && this.trees.blocksStep(x0Mm, y0Mm, x1Mm, y1Mm))
      || (this.buildings !== null && this.buildings.blocksStep(x0Mm, y0Mm, x1Mm, y1Mm));
  }

  avoidStep(x0Mm: number, y0Mm: number, dxMm: number, dyMm: number): [number, number] | null {
    return avoidObjects([this.trees, this.buildings], x0Mm, y0Mm, dxMm, dyMm);
  }

  /** Read-only view for the renderer, which draws the grid directly. */
  get cells(): Uint8Array {
    return this.flags;
  }

  // ------------------------------------------------------------------ loading

  static decode(blob: ArrayBuffer | Uint8Array): CollisionMap {
    const bytes = blob instanceof Uint8Array ? blob : new Uint8Array(blob);
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    if (bytes.byteLength < HEADER_BYTES) {
      throw new TerrainError(`terrain blob is ${bytes.byteLength} bytes`);
    }
    if (view.getUint32(0, false) !== MAGIC) {
      throw new TerrainError("terrain blob has the wrong magic");
    }
    const version = view.getUint16(4, true);
    if (version !== VERSION) {
      throw new TerrainError(`terrain blob version ${version}, expected ${VERSION}`);
    }

    const patchesX = view.getUint16(6, true);
    const patchesY = view.getUint16(8, true);
    const grid = view.getUint16(10, true);
    const cellMm = view.getUint32(12, true);
    const baseXMm = view.getInt32(16, true);
    const baseYMm = view.getInt32(20, true);
    const runCount = view.getUint32(24, true);
    const cellCount = view.getUint32(28, true);

    if (!(cellMm > 0) || !(grid > 0)) {
      throw new TerrainError("terrain blob has a degenerate cell size or grid");
    }
    if (patchesX * grid * patchesY * grid !== cellCount) {
      throw new TerrainError("terrain blob dimensions disagree with its cell count");
    }
    if (cellCount > MAX_CELLS) {
      throw new TerrainError(`terrain blob claims ${cellCount} cells, over the cap`);
    }
    if (bytes.byteLength < HEADER_BYTES + runCount * 4) {
      throw new TerrainError("terrain blob is truncated mid-run");
    }

    const flags = new Uint8Array(cellCount);
    let at = 0;
    for (let i = 0; i < runCount; i++) {
      const offset = HEADER_BYTES + i * 4;
      const value = bytes[offset];
      // u24 length, low byte first.
      const length =
        bytes[offset + 1] | (bytes[offset + 2] << 8) | (bytes[offset + 3] << 16);
      if (at + length > cellCount) {
        throw new TerrainError("terrain blob runs overflow the grid");
      }
      if (value !== 0) flags.fill(value, at, at + length);
      at += length;
    }
    if (at !== cellCount) {
      throw new TerrainError(`terrain blob covers ${at} of ${cellCount} cells`);
    }

    return new CollisionMap({
      patchesX,
      patchesY,
      flags,
      baseXMm,
      baseYMm,
      cellMm,
      grid,
    });
  }

  /** Fetch and decode the blob served from `public/`. */
  static async fetch(url: string): Promise<CollisionMap> {
    const response = await fetch(url);
    if (!response.ok) {
      throw new TerrainError(`${url}: HTTP ${response.status}`);
    }
    return CollisionMap.decode(await response.arrayBuffer());
  }

  /** An all-walkable map of a given size. Used by tests and by the offline demo. */
  static empty(patchesX: number, patchesY: number): CollisionMap {
    return new CollisionMap({
      patchesX,
      patchesY,
      flags: new Uint8Array(patchesX * ATTR_GRID * patchesY * ATTR_GRID),
    });
  }
}
